import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  Image,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Dimensions,
} from 'react-native';
import { useAuthentication } from '../../hooks/user/useAuthentication';
import CustomAlert from '../Hooks/Alert/Alert';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '../../linkingConfig';
import firebase from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import { useCustomAlert } from '../../hooks/useCustomAlert';
import { useAuthenticatedApi } from '../../hooks/user/useAuthenticatedApi';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faArrowLeft } from '../../optimizations/OptimizedIcons';
import TriBoolean from '../../hooks/utilities/ThreeBoolean';
import { useDebugLogger } from '../../hooks/utilities/useDebugLogger';

// Import the clipdle gif
const clipdleGif = require('../../assets/gifs/clipdle-thinking.webp');

type AuthState = 'login' | 'signup' | 'forgot';

interface AuthModalProps {
  visible: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({
  visible,
  onClose,
  onLoginSuccess,
}) => {
  const [authState, setAuthState] = useState<AuthState>('login');
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { setIsAuthenticated, setUserProfile } = useAuth();
  const { showCustomAlert, hideCustomAlert, alertProps } = useCustomAlert();
  const { authenticatedApiCall, unauthenticatedApiCall } = useAuthenticatedApi();
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);

  // Update screen dimensions when orientation changes
  useEffect(() => {
    const updateDimensions = () => {
      setScreenWidth(Dimensions.get('window').width);
    };
    const dimensionsListener = Dimensions.addEventListener('change', updateDimensions);
    
    return () => {
      // Clean up the event listener when component unmounts
      dimensionsListener.remove();
    };
  }, []);

  // Determine if we're on a small screen
  const isSmallScreen = screenWidth < 375;

  // Login states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Sign up states
  const [signupStep, setSignupStep] = useState(1);
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const debug = useDebugLogger();

  const {
    loading,
    handleLogin,
    alertVisible,
    setAlertVisible,
    alertTitle,
    alertMessage,
    handleForgotPassword,
  } = useAuthentication();

  // Login handler
  const attemptLogin = async () => {
    const success = await handleLogin(email, password);
    if (success) {
      onClose();
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  // Reset all states
  const resetStates = () => {
    setEmail('');
    setPassword('');
    setUsername('');
    setName('');
    setBio('');
    setSignupStep(1);
  };

  // Change auth state
  const changeAuthState = (newState: AuthState) => {
    resetStates();
    setAuthState(newState);
  };

  // Validation functions
  const isValidEmail = () => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegex.test(email);
  };

  const isValidPassword = () => {
    let errorMessage = '';

    if (password.length < 8 || password.length > 20) {
      errorMessage += 'Password must be 8-20 characters long.\n';
    }
    if (!/[a-z]/.test(password)) {
      errorMessage += 'Password must include at least one lowercase letter.\n';
    }
    if (!/[A-Z]/.test(password)) {
      errorMessage += 'Password must include at least one uppercase letter.\n';
    }
    if (!/\d/.test(password)) {
      errorMessage += 'Password must include at least one number.\n';
    }
    if (!/[@$!%#*?&]/.test(password)) {
      errorMessage += 'Password must include at least one special character (@, $, !, %, *, ?, &).\n';
    }

    if (errorMessage) {
      showCustomAlert('Invalid Password', errorMessage);
      return false;
    }

    return true;
  };

  const isValidName = () => {
    return name.length > 0 && name.length <= 30;
  };

  const isValidBio = () => {
    return bio.length <= 140;
  };

  const checkUserExists = async () => {
    try {
      if(username){
        const userExistsResponse = await unauthenticatedApiCall(`users/exists/${username}`, 'GET' );
        return userExistsResponse.exists;
      } else {
        showCustomAlert("Blank Username", "Please enter a username and try again");
      }
    } catch (error: any) {
      debug.error("Error encountered", error.response);
    }
  };

  const checkEmailExists = async () => {
    try {
      if(email){
        const emailExistsResponse = await unauthenticatedApiCall(`users/exists/${email}`, 'GET' );
        return emailExistsResponse.exists;
      } else {
        showCustomAlert("Email in use", "Email in use, please enter another email or sign in.");
      }
    } catch (error: any) {
      debug.error("Error encountered", error.response);
    }
  };

  // Sign up flow
  const handleSignupStep = async () => {
    if (signupStep < 5) {
      if (signupStep === 1) {
        if (username.length < 3 || username.length > 20) {
          showCustomAlert("Invalid Username", "Username must be 3-20 characters long.");
        } else {
          const userExists = await checkUserExists();
          if (userExists) {
            showCustomAlert("Error", "Username is taken, please enter another username");
          } else {
            setSignupStep(signupStep + 1);
          }
        }
      } else if (signupStep === 2) {
        if (isValidEmail()) {
          const emailExists = await checkEmailExists();
          if (emailExists) {
            showCustomAlert("Email Taken", "It looks like that email is already in use, please use another or log in");
          } else {
            setSignupStep(signupStep + 1);
          }
        } else {
          showCustomAlert("Invalid email", "Please enter a valid email");
        }
      } else if (signupStep === 3) {
        const validPassword = isValidPassword();
        if (validPassword) {
          setSignupStep(signupStep + 1);
        }
      } else if (signupStep === 4) {
        if (!isValidName()) {
          showCustomAlert("Invalid Name", "Name must be between 1 and 30 characters long.");
        } else {
          setSignupStep(signupStep + 1);
        }
      }
    } else {
      if (!isValidBio()) {
        showCustomAlert("Invalid Bio", "Bio must be less than 140 characters long.");
      } else {
        handleSignUp();
      }
    }
  };

  const handleSignUp = async () => {
    try {
      var userCredential = null;
      try {
        userCredential = await createUserWithEmailAndPassword(firebase.auth, email, password);
      } catch (error: any) {
        debug.error("Error encountered", error.response);
      }

      if (userCredential) {
        const data = await unauthenticatedApiCall('users', 'POST', {
          auth0Id: userCredential.user.uid,
          email: email,
          username: username,
          name: name,
          bio: bio,
        });
        if (data) {
          setUserProfile(data);
          setIsAuthenticated(TriBoolean.TRUE);
          onClose();
        }
      }
    } catch (error: string | any) {
      showCustomAlert('Signup Error', 'There was an error signing up. Please try again.');
    }
  };

  // Forgot password handler
  const handleForgotPasswordSubmit = () => {
    handleForgotPassword(email);
  };

  // Render login content
  const renderLoginContent = () => (
    <>
      <View style={styles.titleContainer}>
        <Text style={styles.title}>Clipdle</Text>
        <Image source={clipdleGif} style={styles.gifStyle} />
      </View>
      <Text style={styles.slogan}>Not Just Watching</Text>

      <TextInput
        style={styles.input}
        onChangeText={setEmail}
        value={email}
        placeholder="Email"
        placeholderTextColor={'black'}
        keyboardType="email-address"
        autoCapitalize="none"
      />
      <TextInput
        style={styles.input}
        onChangeText={setPassword}
        value={password}
        placeholder="Password"
        placeholderTextColor={'black'}
        secureTextEntry
      />

      <Pressable
        style={styles.loginButton}
        onPress={attemptLogin}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator size="small" color="#FFFFFF" />
        ) : (
          <Text style={styles.buttonText}>Log In</Text>
        )}
      </Pressable>

      <Pressable 
        style={styles.forgotPasswordWrapper} 
        onPress={() => changeAuthState('forgot')}
      >
        <Text style={styles.forgotPasswordText}>
          Forgot password?
        </Text>
      </Pressable>
      
      <Pressable 
        style={styles.signUpWrapper}
        onPress={() => changeAuthState('signup')}
      >
        <Text style={styles.signUpLink}>
          Sign Up
        </Text>
      </Pressable>
    </>
  );

  // Render signup step content
  const renderSignupStepContent = () => {
    switch (signupStep) {
      case 1:
        return (
          <>
            <Text style={styles.headerText}>Create username</Text>
            <Text style={styles.subHeaderText}>This will be how other users see you</Text>
            <TextInput
              style={styles.input}
              value={username}
              onChangeText={(text) => setUsername(text.substring(0, 30))}
              placeholder="Create username"
              placeholderTextColor="white"
              maxLength={30}
            />
          </>
        );
      case 2:
        return (
          <>
            <Text style={styles.headerText}>Enter Email</Text>
            <Text style={styles.subHeaderText}>Used to log into your account</Text>
            <TextInput
              style={styles.input}
              value={email}
              onChangeText={setEmail}
              placeholder="Enter email"
              placeholderTextColor="white"
              keyboardType="email-address"
            />
          </>
        );
      case 3:
        return (
          <>
            <Text style={styles.headerText}>Create Password</Text>
            <Text style={styles.subHeaderText}>Choose a strong password</Text>
            <TextInput
              style={styles.input}
              value={password}
              onChangeText={setPassword}
              placeholder="Choose a password"
              placeholderTextColor="white"
              secureTextEntry
            />
          </>
        );
      case 4:
        return (
          <>
            <Text style={styles.headerText}>Your Name</Text>
            <Text style={styles.subHeaderText}>Let people know who you are</Text>
            <TextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              placeholder="Enter your name"
              placeholderTextColor="white"
            />
          </>
        );
      case 5:
        return (
          <>
            <Text style={styles.headerText}>Add a Bio</Text>
            <Text style={styles.subHeaderText}>Share about yourself (optional)</Text>
            <TextInput
              style={styles.input}
              value={bio}
              onChangeText={setBio}
              placeholder="Add a bio (optional)"
              placeholderTextColor="white"
            />
          </>
        );
      default:
        return null;
    }
  };

  // Render signup content
  const renderSignupContent = () => (
    <>
      <View style={styles.backButtonContainer}>
        <Pressable 
          onPress={() => signupStep === 1 ? changeAuthState('login') : setSignupStep(signupStep - 1)}
          style={styles.backButton}
        >
          <FontAwesomeIcon icon={faArrowLeft} color='white' size={24} />
        </Pressable>
      </View>

      <View style={styles.signupContentContainer}>
        {renderSignupStepContent()}
      </View>

      <Pressable
        style={styles.loginButton}
        onPress={handleSignupStep}
      >
        <Text style={styles.buttonText}>
          {signupStep === 5 ? "Sign Up" : "Next"}
        </Text>
      </Pressable>
    </>
  );

  // Render forgot password content
  const renderForgotPasswordContent = () => (
    <>
      <View style={styles.backButtonContainer}>
        <Pressable 
          onPress={() => changeAuthState('login')}
          style={styles.backButton}
        >
          <FontAwesomeIcon icon={faArrowLeft} color='white' size={24} />
        </Pressable>
      </View>

      <View style={styles.forgotContentContainer}>
        <Text style={styles.headerText}>Reset Password</Text>
        <Text style={styles.subHeaderText}>
          We'll send reset instructions to your email
        </Text>
        <TextInput
          style={styles.input}
          value={email}
          onChangeText={setEmail}
          placeholder="Enter your email"
          placeholderTextColor="white"
          keyboardType="email-address"
          autoCapitalize="none"
        />

        <Pressable
          style={styles.loginButton}
          onPress={handleForgotPasswordSubmit}
        >
          <Text style={styles.buttonText}>Send Email</Text>
        </Pressable>
      </View>
    </>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalBackground}>
        <View style={[styles.modalContainer, isSmallScreen && styles.modalContainerSmall]}>
          {/* Alerts */}
          <CustomAlert
            title={alertTitle}
            message={alertMessage}
            isVisible={alertVisible}
            onClose={() => setAlertVisible(false)}
            buttons={[
              {
                text: 'OK',
                onPress: () => setAlertVisible(false),
              },
            ]}
          />
          <CustomAlert {...alertProps} />

          <KeyboardAvoidingView 
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            style={styles.contentContainer}
          >
            <ScrollView 
              contentContainerStyle={styles.scrollContent}
              keyboardShouldPersistTaps="handled"
            >
              {authState === 'login' && renderLoginContent()}
              {authState === 'signup' && renderSignupContent()}
              {authState === 'forgot' && renderForgotPasswordContent()}
            </ScrollView>
          </KeyboardAvoidingView>

          <Pressable onPress={onClose} style={styles.closeButton}>
            <Text style={styles.closeButtonText}>CANCEL</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
};

export default AuthModal;

const styles = StyleSheet.create({
  modalBackground: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 420,
    backgroundColor: 'black',
    borderRadius: 10,
    alignItems: 'center',
    padding: 25,
    maxHeight: '90%',
  },
  modalContainerSmall: {
    width: '95%',
    padding: 20,
  },
  contentContainer: {
    width: '100%',
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    paddingTop: 40,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 20,
    width: '100%',
    justifyContent: 'center',
    backgroundColor: '#2069e0',
    marginBottom: 10,
  },
  title: {
    fontSize: Platform.OS === 'ios' ? 28 : 26,
    fontFamily: 'PressStart2P_400Regular',
    color: '#f4d47c',
    fontWeight: 'bold',
  },
  gifStyle: {
    width: 45,
    height: 45,
    marginLeft: 10,
  },
  slogan: {
    fontSize: Platform.OS === 'ios' ? 14 : 13,
    fontFamily: 'PressStart2P_400Regular',
    maxWidth: '80%',
    color: '#bebebe',
    fontStyle: 'italic',
    marginBottom: 24,
    marginTop: 8,
    textAlign: 'center',
  },
  headerText: {
    fontSize: Platform.OS === 'ios' ? 22 : 20,
    fontFamily: 'PressStart2P_400Regular',
    color: 'white',
    marginBottom: 8,
    textAlign: 'center',
    maxWidth: '90%',
    lineHeight: Platform.OS === 'ios' ? 30 : 28,
  },
  subHeaderText: {
    fontSize: Platform.OS === 'ios' ? 14 : 13,
    fontFamily: 'PressStart2P_400Regular',
    color: '#bebebe',
    marginBottom: 20,
    textAlign: 'center',
    maxWidth: '85%',
    lineHeight: Platform.OS === 'ios' ? 20 : 18,
  },
  input: {
    width: '90%',
    maxWidth: 320,
    fontFamily: 'PressStart2P_400Regular',
    padding: 12,
    marginVertical: 6,
    backgroundColor: 'white',
    borderRadius: 5,
    fontSize: 14,
  },
  loginButton: {
    paddingHorizontal: 25,
    paddingVertical: 12,
    backgroundColor: '#2069e0',
    borderRadius: 30,
    marginTop: 20,
  },
  buttonText: {
    fontSize: 16,
    fontFamily: 'PressStart2P_400Regular',
    color: '#ffffff',
    fontWeight: 'bold',
  },
  forgotPasswordWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 12,
    paddingHorizontal: 10,
  },
  forgotPasswordText: {
    color: '#f4d47c',
    fontSize: Platform.OS === 'ios' ? 14 : 13,
    fontFamily: 'PressStart2P_400Regular',
    fontWeight: 'bold',
    textDecorationLine: 'underline',
    textAlign: 'center',
  },
  signUpWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    paddingHorizontal: 10,
  },
  signUpLink: {
    color: '#f4d47c',
    fontFamily: 'PressStart2P_400Regular',
    fontSize: Platform.OS === 'ios' ? 14 : 13,
    fontWeight: 'bold',
    textDecorationLine: 'underline',
    textAlign: 'center',
  },
  closeButton: {
    marginTop: 15,
    paddingHorizontal: 15,
    paddingVertical: 10,
    backgroundColor: '#555', 
    borderRadius: 5,
  },
  closeButtonText: {
    color: 'white',
    fontSize: 14,
  },
  backButtonContainer: {
    position: 'absolute',
    top: 10,
    left: 15,
    zIndex: 1,
  },
  backButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  signupContentContainer: {
    width: '100%',
    alignItems: 'center',
    marginTop: 20,
    paddingHorizontal: 10,
  },
  forgotContentContainer: {
    width: '100%',
    alignItems: 'center',
    marginTop: 20,
    paddingHorizontal: 10,
  },
});